import React from 'react'

interface LazyLoaderProps {
  fallback?: React.ReactNode
}

export default function LazyLoader({ fallback }: LazyLoaderProps) {
  if (fallback) {
    return <>{fallback}</>
  }

  return (
    <div className="min-h-screen bg-dark-primary text-text-primary flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
        <p className="text-text-secondary">Loading...</p>
      </div>
    </div>
  )
}

// Page loading skeleton for better UX
export function PageSkeleton() {
  return (
    <div className="p-6 space-y-6 animate-pulse">
      {/* Header skeleton */}
      <div className="flex justify-between items-center">
        <div className="h-8 bg-dark-secondary rounded w-48"></div>
        <div className="h-10 bg-dark-secondary rounded w-32"></div>
      </div>
      
      {/* Content skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-dark-secondary rounded-lg p-6 space-y-4">
            <div className="h-4 bg-dark-tertiary rounded w-3/4"></div>
            <div className="h-6 bg-dark-tertiary rounded w-1/2"></div>
            <div className="h-4 bg-dark-tertiary rounded w-2/3"></div>
          </div>
        ))}
      </div>
      
      {/* Table skeleton */}
      <div className="bg-dark-secondary rounded-lg overflow-hidden">
        <div className="h-12 bg-dark-tertiary"></div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 border-t border-dark-tertiary bg-dark-secondary">
            <div className="flex items-center h-full px-6 space-x-4">
              <div className="h-4 bg-dark-tertiary rounded flex-1"></div>
              <div className="h-4 bg-dark-tertiary rounded w-24"></div>
              <div className="h-4 bg-dark-tertiary rounded w-32"></div>
              <div className="h-8 bg-dark-tertiary rounded w-20"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Calendar loading skeleton
export function CalendarSkeleton() {
  return (
    <div className="p-6 space-y-6 animate-pulse">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="h-8 bg-dark-secondary rounded w-40"></div>
        <div className="flex space-x-2">
          <div className="h-10 bg-dark-secondary rounded w-24"></div>
          <div className="h-10 bg-dark-secondary rounded w-24"></div>
        </div>
      </div>
      
      {/* Calendar grid */}
      <div className="bg-dark-secondary rounded-lg p-4">
        {/* Calendar header */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="h-8 bg-dark-tertiary rounded flex items-center justify-center">
              <div className="h-3 bg-dark-primary rounded w-6"></div>
            </div>
          ))}
        </div>
        
        {/* Calendar body */}
        <div className="grid grid-cols-7 gap-1">
          {[...Array(35)].map((_, i) => (
            <div key={i} className="aspect-square bg-dark-tertiary rounded p-2">
              <div className="h-4 bg-dark-primary rounded w-6 mb-2"></div>
              {Math.random() > 0.7 && (
                <div className="h-2 bg-gold rounded w-full opacity-30"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// List loading skeleton
export function ListSkeleton({ itemCount = 10 }: { itemCount?: number }) {
  return (
    <div className="space-y-3 animate-pulse">
      {[...Array(itemCount)].map((_, i) => (
        <div key={i} className="bg-dark-secondary rounded-lg p-4 flex items-center space-x-4">
          <div className="w-12 h-12 bg-dark-tertiary rounded-full flex-shrink-0"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-dark-tertiary rounded w-3/4"></div>
            <div className="h-3 bg-dark-tertiary rounded w-1/2"></div>
          </div>
          <div className="h-8 bg-dark-tertiary rounded w-20"></div>
        </div>
      ))}
    </div>
  )
}

// Dashboard skeleton
export function DashboardSkeleton() {
  return (
    <div className="p-6 space-y-6 animate-pulse">
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-dark-secondary rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="h-4 bg-dark-tertiary rounded w-20"></div>
              <div className="w-8 h-8 bg-dark-tertiary rounded"></div>
            </div>
            <div className="h-8 bg-dark-tertiary rounded w-16"></div>
            <div className="h-3 bg-dark-tertiary rounded w-24"></div>
          </div>
        ))}
      </div>
      
      {/* Chart area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-dark-secondary rounded-lg p-6 space-y-4">
          <div className="h-6 bg-dark-tertiary rounded w-40"></div>
          <div className="h-64 bg-dark-tertiary rounded"></div>
        </div>
        <div className="bg-dark-secondary rounded-lg p-6 space-y-4">
          <div className="h-6 bg-dark-tertiary rounded w-40"></div>
          <div className="h-64 bg-dark-tertiary rounded"></div>
        </div>
      </div>
    </div>
  )
}

// Form skeleton
export function FormSkeleton({ fieldCount = 6 }: { fieldCount?: number }) {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-dark-secondary rounded w-48"></div>
      
      {[...Array(fieldCount)].map((_, i) => (
        <div key={i} className="space-y-2">
          <div className="h-4 bg-dark-tertiary rounded w-24"></div>
          <div className="h-10 bg-dark-secondary rounded w-full"></div>
        </div>
      ))}
      
      <div className="flex space-x-3 pt-4">
        <div className="h-10 bg-gold rounded w-24 opacity-30"></div>
        <div className="h-10 bg-dark-tertiary rounded w-20"></div>
      </div>
    </div>
  )
}

// Table skeleton
export function TableSkeleton({ 
  columns = 4, 
  rows = 8,
  showHeader = true 
}: { 
  columns?: number
  rows?: number
  showHeader?: boolean 
}) {
  return (
    <div className="bg-dark-secondary rounded-lg overflow-hidden animate-pulse">
      {showHeader && (
        <div className="h-12 bg-dark-tertiary border-b border-dark-primary">
          <div className="flex items-center h-full px-6 space-x-4">
            {[...Array(columns)].map((_, i) => (
              <div key={i} className="h-4 bg-dark-primary rounded flex-1"></div>
            ))}
          </div>
        </div>
      )}
      
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="h-16 border-t border-dark-tertiary">
          <div className="flex items-center h-full px-6 space-x-4">
            {[...Array(columns)].map((_, j) => (
              <div 
                key={j} 
                className={`h-4 bg-dark-tertiary rounded ${
                  j === 0 ? 'flex-2' : j === columns - 1 ? 'w-20' : 'flex-1'
                }`}
              ></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// Card skeleton
export function CardSkeleton({ 
  showImage = false,
  showActions = true,
  className = ""
}: { 
  showImage?: boolean
  showActions?: boolean
  className?: string
}) {
  return (
    <div className={`bg-dark-secondary rounded-lg p-6 space-y-4 animate-pulse ${className}`}>
      {showImage && (
        <div className="h-32 bg-dark-tertiary rounded"></div>
      )}
      
      <div className="space-y-3">
        <div className="h-6 bg-dark-tertiary rounded w-3/4"></div>
        <div className="h-4 bg-dark-tertiary rounded w-full"></div>
        <div className="h-4 bg-dark-tertiary rounded w-2/3"></div>
      </div>
      
      {showActions && (
        <div className="flex space-x-2 pt-4">
          <div className="h-8 bg-dark-tertiary rounded w-20"></div>
          <div className="h-8 bg-dark-tertiary rounded w-16"></div>
        </div>
      )}
    </div>
  )
}

// Invoice skeleton
export function InvoiceSkeleton() {
  return (
    <div className="bg-dark-secondary rounded-lg p-6 space-y-6 animate-pulse">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="space-y-2">
          <div className="h-8 bg-dark-tertiary rounded w-40"></div>
          <div className="h-4 bg-dark-tertiary rounded w-32"></div>
        </div>
        <div className="h-6 bg-dark-tertiary rounded w-20"></div>
      </div>
      
      {/* Customer info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="h-5 bg-dark-tertiary rounded w-16"></div>
          <div className="h-4 bg-dark-tertiary rounded w-48"></div>
          <div className="h-4 bg-dark-tertiary rounded w-40"></div>
        </div>
        <div className="space-y-3">
          <div className="h-5 bg-dark-tertiary rounded w-20"></div>
          <div className="h-4 bg-dark-tertiary rounded w-32"></div>
          <div className="h-4 bg-dark-tertiary rounded w-28"></div>
        </div>
      </div>
      
      {/* Line items */}
      <div className="space-y-4">
        <div className="h-5 bg-dark-tertiary rounded w-24"></div>
        <TableSkeleton columns={4} rows={3} showHeader={true} />
      </div>
      
      {/* Total */}
      <div className="flex justify-end">
        <div className="space-y-2 w-64">
          <div className="flex justify-between">
            <div className="h-4 bg-dark-tertiary rounded w-16"></div>
            <div className="h-4 bg-dark-tertiary rounded w-20"></div>
          </div>
          <div className="flex justify-between">
            <div className="h-4 bg-dark-tertiary rounded w-12"></div>
            <div className="h-4 bg-dark-tertiary rounded w-16"></div>
          </div>
          <div className="flex justify-between border-t border-dark-tertiary pt-2">
            <div className="h-5 bg-dark-tertiary rounded w-16"></div>
            <div className="h-5 bg-gold rounded w-24 opacity-30"></div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Chat/Communication skeleton
export function CommunicationSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {[...Array(5)].map((_, i) => (
        <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
          <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg space-y-2 ${
            i % 2 === 0 ? 'bg-dark-secondary' : 'bg-gold bg-opacity-20'
          }`}>
            <div className="h-4 bg-dark-tertiary rounded w-3/4"></div>
            <div className="h-4 bg-dark-tertiary rounded w-1/2"></div>
            <div className="h-3 bg-dark-tertiary rounded w-16"></div>
          </div>
        </div>
      ))}
    </div>
  )
}

// Settings skeleton
export function SettingsSkeleton() {
  return (
    <div className="space-y-8 animate-pulse">
      {[...Array(4)].map((_, sectionIndex) => (
        <div key={sectionIndex} className="bg-dark-secondary rounded-lg p-6">
          <div className="h-6 bg-dark-tertiary rounded w-48 mb-6"></div>
          
          <div className="space-y-4">
            {[...Array(3)].map((_, itemIndex) => (
              <div key={itemIndex} className="flex items-center justify-between py-3 border-b border-dark-tertiary last:border-b-0">
                <div className="flex-1">
                  <div className="h-4 bg-dark-tertiary rounded w-32 mb-1"></div>
                  <div className="h-3 bg-dark-tertiary rounded w-48"></div>
                </div>
                <div className="h-6 bg-dark-tertiary rounded w-12"></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// Chart skeleton
export function ChartSkeleton({ 
  title = true,
  legend = true,
  height = 64 
}: {
  title?: boolean
  legend?: boolean
  height?: number
}) {
  return (
    <div className="bg-dark-secondary rounded-lg p-6 space-y-4 animate-pulse">
      {title && (
        <div className="h-6 bg-dark-tertiary rounded w-40"></div>
      )}
      
      <div className={`h-${height} bg-dark-tertiary rounded`}></div>
      
      {legend && (
        <div className="flex justify-center space-x-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-dark-tertiary rounded"></div>
              <div className="h-3 bg-dark-tertiary rounded w-16"></div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Navigation skeleton
export function NavigationSkeleton() {
  return (
    <div className="w-72 bg-black/80 backdrop-blur-lg border-r-2 border-border-gold p-5 space-y-6 animate-pulse">
      {/* Logo */}
      <div className="text-center border-b border-border-gold pb-8 mb-5">
        <div className="h-8 bg-dark-tertiary rounded w-32 mx-auto mb-1"></div>
        <div className="h-4 bg-dark-tertiary rounded w-24 mx-auto"></div>
      </div>
      
      {/* Navigation sections */}
      {[...Array(4)].map((_, sectionIndex) => (
        <div key={sectionIndex} className="space-y-3">
          <div className="h-4 bg-dark-tertiary rounded w-20"></div>
          
          <div className="space-y-1">
            {[...Array(3)].map((_, itemIndex) => (
              <div key={itemIndex} className="flex items-center space-x-3 p-3 rounded-lg">
                <div className="w-5 h-5 bg-dark-tertiary rounded"></div>
                <div className="h-4 bg-dark-tertiary rounded flex-1"></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}